// Footer.jsx
"use client";

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Link, Stack, Tooltip, Chip, Divider } from '@mui/material';
import { SiCreativecommons } from "react-icons/si";
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';

export default function Footer() {
  const { t } = useTranslation('common');

  // 今日の日付を "YYYY-MM-DD" 形式で取得
  const today = new Date().toISOString().slice(0, 10);

  // i18nextの補間機能を使って引用文を生成
  const citationText = t('footer.citation', { date: today });

  const currentYear = new Date().getFullYear();

  return (
    <Box
      component="footer"
      sx={{
        mt: 6,
        py: 4,
        px: 2,
        backgroundColor: 'grey.900',
        color: 'grey.300',
        textAlign: 'center',
      }}
    >
      {/* サイト名 */}
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
        {t('footer.siteName')}
      </Typography>

      {/* 利用について */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>{t('footer.sectionUsage')}</Typography>
        <Stack direction="row" justifyContent="center" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <SiCreativecommons size={24} />
          <Typography variant="body2">
            <Link
              href="https://creativecommons.org/licenses/by/4.0/"
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              color="inherit"
            >
              CC BY 4.0
            </Link>{" "}
            - {t('footer.license')}
          </Typography>
        </Stack>
        <Typography
          variant="caption"
          sx={{ display: "block", maxWidth: 640, mx: "auto", opacity: 0.8 }}
        >
          {t('footer.description')}
        </Typography>
        <Box sx={{ mt: 1.5 }}>
          <Link
            href="https://creativecommons.org/licenses/by/4.0/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src="https://licensebuttons.net/l/by/4.0/88x31.png"
              alt="Creative Commons License"
              style={{ height: 36 }}
            />
          </Link>
        </Box>
      </Box>

      <Divider sx={{ my: 3, borderColor: "rgba(255,255,255,0.1)" }} />

      {/* 引用方法 */}
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          {t('footer.sectionCitation')}
        </Typography>
        <Stack direction="row" justifyContent="center" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <FormatQuoteIcon fontSize="small" />
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            {t('footer.howToCite')}
          </Typography>
        </Stack>
        <Typography
          variant="caption"
          sx={{ display: "block", maxWidth: 640, mx: "auto", opacity: 0.9 }}
        >
          {citationText}
        </Typography>
      </Box>

      <Divider sx={{ my: 3, borderColor: "rgba(255,255,255,0.1)" }} />

      {/* 支援 */}
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          {t('footer.sectionSupport')}
        </Typography>
        <Tooltip title={t('footer.supportTooltip')} arrow>
          <Link
            href="https://www.jsps.go.jp/j-grantsinaid/"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ display: "inline-block" }}
          >
            <Box
              component="img"
              src="/KAKENHIlogo_S.jpg"
              alt="JSPS KAKENHI"
              sx={{ height: 60, objectFit: "contain", borderRadius: 1 }}
            />
          </Link>
        </Tooltip>
        <Box sx={{ mt: 1, display: "flex", justifyContent: "center", gap: 1, flexWrap: "wrap" }}>
          <Chip
            label="JP23KJ2170 for So Shimizu"
            component="a"
            href="https://kaken.nii.ac.jp/ja/grant/KAKENHI-PROJECT-23KJ2170/"
            target="_blank"
            rel="noopener noreferrer"
            clickable
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 700 }}
          />
        </Box>
      </Box>

      {/* コピーライト */}
      <Typography variant="caption" display="block" sx={{ mt: 2, opacity: 0.6 }}>
        © {currentYear} {t('footer.siteName')}. {t('footer.copyright')}
      </Typography>
    </Box>
  );
}
